import type { Rubric } from './rubric';

/**
 * What a teacher pastes into "Build with AI", and what comes back. Pure. There is no
 * table parser: rubrics arrive as PDFs, lists, prose and merged cells, and exact-format
 * rules failed teachers. AI reads the text (`core/structure.ts`); the teacher reviews it.
 */

export type Grid = string[][];

/** Only AI builds a rubric from pasted text. Kept as a field so the event shape is stable. */
export type PasteSource = 'ai';

export type PasteResult =
  | { ok: true; rubric: Rubric; warnings: string[] }
  | { ok: false; reason: 'empty' | 'not_a_table' | 'too_few_levels' | 'too_many_levels' };

export const PASTE_ERRORS: Record<Exclude<PasteResult, { ok: true }>['reason'], string> = {
  empty: 'Paste your rubric first.',
  not_a_table: 'AI could not find criteria and levels in that. Paste the whole rubric, including the level names.',
  too_few_levels: 'AI found fewer than two performance levels. Paste the whole rubric, including the level names.',
  too_many_levels: 'AI found more than six performance levels. Remove some and try again.',
};

const clean = (cell: string) => cell.replace(/\u00a0/g, ' ').replace(/[ \t]+/g, ' ').replace(/\s*\n\s*/g, ' ').trim();

/** A table copied from Docs, Word or Sheets, as tab-separated lines, so AI sees the columns. */
export const gridToText = (grid: Grid): string =>
  grid.map(row => row.map(clean).join('\t')).filter(line => line.trim()).join('\n');

/** Words teachers use for levels, ranked low (1) to high (4). */
const RANKS: [RegExp, number][] = [
  [/\b(beginning|emerging|novice|insufficient|below|not yet|incomplete|unsatisfactory|minimal|limited)\b/i, 1],
  [/\b(developing|approaching|partial(ly)?|basic|progressing|needs improvement)\b/i, 2],
  [/\b(proficient|meets|competent|accomplished|satisfactory|good|secure)\b/i, 3],
  [/\b(exemplary|advanced|exceeds|mastery|distinguished|excellent|outstanding|extending)\b/i, 4],
];

const rankOf = (name: string): number | null => {
  for (const [pattern, rank] of RANKS) if (pattern.test(name)) return rank;
  return null;
};

/** The level a teacher most likely means by "passing": Proficient or its synonym, else the middle. */
export function defaultPassLevel(names: string[]): number {
  const proficient = names.findIndex(name => rankOf(name) === 3 && !/\d/.test(name));
  return proficient >= 0 ? proficient : Math.floor(names.length / 2);
}
